import stringify from 'json-stringify-safe';
import { serializeError } from 'serialize-error';
import { ResponseError, InternalErrorEnum } from '@landr/core.models';
import { CommonEventIds } from './CommonEventIds.es.js';
import { LogLevel } from './types/LogLevel.es.js';
import { AllowedErrorFieldsEnum } from './types/AllowedErrorFieldsEnum.es.js';
import { AllowedExtraInfosFields } from './types/AllowedExtraInfosFields.es.js';

const faultyBrowserExtensions = [
    'https://js.intercomcdn.com/',
    'fww.hieroglyphstwittering.com',
    'zib.slakelegionary.com',
    'pageantryundertakings.com',
    'attacksspaghetti.com',
    'journallingpercolates.com',
    'jrc.withstandingheartwarming.com',
    'nbz.deferringplateaus.com',
    'demisedcolonnaded.com',
    'akamaihd.net',
];
const logLevelToNumericValueMap = {
    [LogLevel.LOG]: 0,
    [LogLevel.DEBUG]: 1,
    [LogLevel.INFO]: 2,
    [LogLevel.WARN]: 3,
    [LogLevel.ERROR]: 4,
    [LogLevel.FATAL]: 5,
};
function getLogLevel(value) {
    return logLevelToNumericValueMap[value];
}
function isInstanceOfApiResponseError(obj) {
    return obj instanceof ResponseError;
}
function isNil(value) {
    return value === null || value === undefined;
}
function isObject(value) {
    return typeof value === 'object';
}
function isError(value) {
    if (!value || !isObject(value)) {
        return false;
    }
    const tag = Object.prototype.toString.call(value);
    return (tag === '[object Error]' ||
        tag === '[object DOMException]' ||
        (typeof value.message === 'string' && typeof value.name === 'string'));
}
function getError(firstParam) {
    let error = null;
    if (firstParam) {
        if (isError(firstParam) || isInstanceOfApiResponseError(firstParam)) {
            error = firstParam;
        }
    }
    return error;
}
function getLogLevelForError(error, actualLevel) {
    // Very simple for now
    // Later will handle some backend known codes, some 404, etc.
    if (error.status) {
        const isInternalError = Object.values(InternalErrorEnum).includes(error.status);
        if (isInternalError) {
            return LogLevel.DEBUG;
        }
    }
    return actualLevel;
}
function getRealLogLevel(args, level) {
    const firstParam = args.length > 2 ? args[2] : null;
    // Compute a new level based on potential error
    const error = getError(firstParam);
    return error ? getLogLevelForError(error, level) : level;
}
function shouldLogToProvider(level, config) {
    const minLevel = getLogLevel(config.minLevel);
    const logLevelEnabled = getLogLevel(level) >= minLevel;
    return config.enabled && logLevelEnabled;
}
function isClassLogEnable(className, level, moduleConfig, valueIfNotFound = true) {
    const classLevel = moduleConfig && moduleConfig[className];
    if (isNil(classLevel)) {
        return valueIfNotFound;
    }
    return getLogLevel(classLevel) <= getLogLevel(level);
}
function isExternalFatalError(level, eventId, error) {
    return (level === LogLevel.FATAL &&
        eventId === CommonEventIds.UnhandledError &&
        isError(error) &&
        typeof error.stack === 'string' &&
        faultyBrowserExtensions.some((ex) => error.stack.indexOf(ex) > -1));
}
function prepareStructuredEvent(level, message, eventId, error, extraInfos, className) {
    // Convert error to an `Error` type if it's a string
    // In loggly, a property should always have the same type, otherwise it's not parsed
    if (typeof error === 'string') {
        error = new Error(error);
    }
    // Ensure error parameter is valid
    if (!isNil(error) && !isError(error) && !isObject(error)) {
        return {
            level: LogLevel.FATAL,
            className,
            eventId: CommonEventIds.UnstructuredError,
            message: '$log can\'t be called with invalid "error" parameter',
            error: undefined,
            extraInfos: { error },
        };
    }
    // Check if error is not from our code
    if (!isNil(error) && isExternalFatalError(level, eventId, error)) {
        return {
            level: LogLevel.WARN,
            className,
            eventId: CommonEventIds.BrowserExtensionsUnhandledError,
            message: 'Browser extensions unhandled error',
            error: error,
            extraInfos: undefined,
        };
    }
    let serializedError = undefined;
    if (error) {
        serializedError = isError(error) ? serializeError(error) : { message: stringify(error) };
    }
    return {
        level,
        className,
        message,
        eventId,
        error: serializedError,
        extraInfos: isNil(extraInfos) ? undefined : extraInfos,
    };
}
function prepareEvent(level, args, className) {
    const message = args[0];
    const eventId = args[1];
    const hasErrorArg = level === LogLevel.FATAL || level === LogLevel.WARN || level === LogLevel.ERROR;
    const error = hasErrorArg ? args[2] : undefined;
    const extraInfos = hasErrorArg ? args[3] : args[2];
    const shouldBeStructuredEvent = level === LogLevel.FATAL || level === LogLevel.WARN || level === LogLevel.ERROR;
    if (typeof message === 'string' && typeof eventId === 'number') {
        return prepareStructuredEvent(level, message, eventId, error, extraInfos, className);
    }
    else if (shouldBeStructuredEvent) {
        // pass all event args as extra information
        return prepareStructuredEvent(LogLevel.FATAL, "$log can't be called with those arguments", CommonEventIds.UnstructuredError, null, { eventArgs: JSON.stringify(args) }, className);
    }
    // TODO: Add EventId for all Info and Debug level logs
    let data = {
        level,
        className,
        message,
    };
    const firstParam = args[1];
    if (firstParam) {
        let logDetails = firstParam;
        if (Array.isArray(logDetails)) {
            logDetails = { params: logDetails };
        }
        else if (logDetails && logDetails.constructor && logDetails instanceof ResponseError) {
            logDetails = { error: logDetails };
        }
        else {
            logDetails = {
                context: isObject(logDetails) ? stringify(logDetails) : logDetails,
            };
        }
        // We create a new object to ensure 'message' will never be overwritten by logDetails one
        data = Object.assign(Object.assign({}, logDetails), data);
    }
    return data;
}
function isStructuredEvent(event) {
    return event.eventId !== undefined;
}
/**
 * Filter a provided Error object and return a new object with the allowed properties ONLY
 * Warning: We stringify each value to avoid busting Loggly max indexing capacity (500, but it index objects recursively)
 * @param error
 */
function filterErrorFields(error) {
    if (typeof error === 'string') {
        return { message: error };
    }
    const newErrorField = {};
    if (error.message) {
        newErrorField.message = stringify(error.message);
    }
    // Doing a try catch as `Object.keys` do not work for all browsers
    // if this fails rawError will still be sent to loggly
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/keys#Polyfill
    try {
        Object.keys(error).forEach((key) => {
            if (Object.values(AllowedErrorFieldsEnum).includes(key)) {
                if (key === AllowedErrorFieldsEnum.detail) {
                    if (error[key].line) {
                        newErrorField['line'] = stringify(error[key].line);
                    }
                    if (error[key].column) {
                        newErrorField['column'] = stringify(error[key].column);
                    }
                    if (error[key].sourceUrl) {
                        newErrorField['sourceUrl'] = stringify(error[key].sourceUrl);
                    }
                }
                else {
                    newErrorField[key] = stringify(error[key]);
                }
            }
        });
    }
    catch (_a) {
        // do nothing it's just to avoid breaking older browsers when logging
    }
    return newErrorField;
}
/**
 * Filter provided AdditionalInfos object and return a new object with the allowed properties ONLY
 * Warning: We stringify each value to avoid busting Loggly max indexing capacity (500, but it index objects recursively)
 * @param ExtraInfos
 */
function filterUnallowedExtraInfosFields(ExtraInfos) {
    const newAdditionalInfos = {};
    try {
        Object.keys(ExtraInfos).forEach((key) => {
            if (Object.values(AllowedExtraInfosFields).includes(key)) {
                if (key === AllowedExtraInfosFields.timeElapsed) {
                    newAdditionalInfos['timeElapsed'] = ExtraInfos[key];
                }
                else {
                    newAdditionalInfos[key] =
                        typeof ExtraInfos[key] === 'string' ? ExtraInfos[key] : stringify(ExtraInfos[key]);
                }
            }
        });
    }
    catch (_a) {
        // do nothing it's just to avoid breaking older browsers when logging
    }
    return newAdditionalInfos;
}

export { filterErrorFields, filterUnallowedExtraInfosFields, getError, getLogLevel, getLogLevelForError, getRealLogLevel, isClassLogEnable, isStructuredEvent, prepareEvent, shouldLogToProvider };
